import { memo, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ResponsiveTree, type CustomSvgLayerProps } from '@nivo/tree';
import { userAppContext } from '@/context/Context';
import fetchEndpoint from '@/services/fetchEndpoint';
import Loader from '@/components/Common/Loader';
import {
  CANOPY_MAX_NEIGHBORS,
  applyCanopyDiseaseNames,
  buildCanopyGraphEndpoint,
  buildCanopyTreeData,
  collectCanopyNodeIds,
  formatCanopyScore,
  parseCanopyGraphPayload,
  resolveCanopyQueryId,
  type CanopyEdge,
  type CanopyResponse,
  type CanopyTreeDatum,
} from '@/utils/canopyTree';
import { fetchDiseaseNames, getCachedDiseaseNames } from '@/utils/diseaseNameCache';

type CanopyTabContentProps = {
  row?: Record<string, unknown> | null;
  className?: string;
};

const QUERY_NODE_COLOR = 'rgb(190, 0, 128)';
const PARENT_NODE_COLOR = '#0369a1';
const CHILD_NODE_COLOR = '#64748b';
const VIA_NODE_COLOR = '#d97706';
const TREE_HEIGHT_PX = 520;
const TREE_MAX_WIDTH_PX = 1100;

function createEdgesLayer(edges: CanopyEdge[], queryId: string, labelsById: Record<string, string>) {
  return function CanopyEdgesLayer({ nodes }: CustomSvgLayerProps<CanopyTreeDatum>) {
    const nodesById = new Map(nodes.map(node => [node.id, node]));

    return (
      <g aria-label="Canopy relationships">
        {edges.map(edge => {
          const source = nodesById.get(edge.parent);
          const target = nodesById.get(edge.child);
          if (!source || !target) return null;

          const isParentRelation = edge.child === queryId;
          const isNestedRelation = edge.parent !== queryId && edge.child !== queryId;
          const color = isParentRelation
            ? PARENT_NODE_COLOR
            : isNestedRelation
              ? VIA_NODE_COLOR
              : CHILD_NODE_COLOR;
          const sameLevel = Math.abs(source.x - target.x) < 1;
          const path = sameLevel
            ? `M ${source.x} ${source.y} C ${source.x - 52} ${source.y}, ${target.x - 52} ${target.y}, ${target.x} ${target.y}`
            : `M ${source.x} ${source.y} C ${(source.x + target.x) / 2} ${source.y}, ${(source.x + target.x) / 2} ${target.y}, ${target.x} ${target.y}`;
          const sourceLabel = labelsById[edge.parent] ?? edge.parent;
          const targetLabel = labelsById[edge.child] ?? edge.child;

          return (
            <path
              key={`${edge.parent}->${edge.child}`}
              data-testid={`canopy-edge-${edge.parent}-${edge.child}`}
              d={path}
              fill="none"
              stroke={color}
              strokeWidth={isNestedRelation ? 2.5 : 2}
              strokeOpacity={0.8}
              strokeDasharray={isNestedRelation ? '5 4' : undefined}
              strokeLinecap="round"
            >
              <title>{`${sourceLabel} → ${targetLabel}`}</title>
            </path>
          );
        })}
      </g>
    );
  };
}

function nodeColor(node: { data: CanopyTreeDatum }): string {
  if (node.data.role === 'query') return QUERY_NODE_COLOR;
  if (node.data.role === 'ancestor' || node.data.linkKind === 'via-neighbor') return VIA_NODE_COLOR;
  if (node.data.linkKind === 'parent') return PARENT_NODE_COLOR;
  return CHILD_NODE_COLOR;
}

function nodeLabel(node: { data: CanopyTreeDatum; id: string }): string {
  return String(node.data.label ?? node.id);
}

type CanopyHoverTooltipState = {
  data: CanopyTreeDatum;
  x: number;
  y: number;
};

const TOOLTIP_OFFSET_PX = 14;
const TOOLTIP_EST_WIDTH_PX = 240;
const TOOLTIP_EST_HEIGHT_PX = 96;

function getTooltipPosition(x: number, y: number): { left: number; top: number } {
  if (typeof window === 'undefined') return { left: x + TOOLTIP_OFFSET_PX, top: y + TOOLTIP_OFFSET_PX };

  const left =
    x + TOOLTIP_OFFSET_PX + TOOLTIP_EST_WIDTH_PX > window.innerWidth
      ? Math.max(8, x - TOOLTIP_EST_WIDTH_PX - TOOLTIP_OFFSET_PX)
      : x + TOOLTIP_OFFSET_PX;
  const top =
    y + TOOLTIP_OFFSET_PX + TOOLTIP_EST_HEIGHT_PX > window.innerHeight
      ? Math.max(8, y - TOOLTIP_EST_HEIGHT_PX - TOOLTIP_OFFSET_PX)
      : y + TOOLTIP_OFFSET_PX;

  return { left, top };
}

function relationLabel(data: CanopyTreeDatum): string {
  const { role, linkKind, viaId } = data;
  if (role === 'query') return 'Query';
  if (role === 'ancestor') return viaId ? `Ancestor via ${viaId}` : 'Ancestor';
  if (linkKind === 'parent') return 'Parent of query';
  if (linkKind === 'via-neighbor') return `Child of ${viaId ?? 'neighbor'}`;
  return 'Child of query';
}

function CanopyHoverTooltip({ hover }: { hover: CanopyHoverTooltipState }) {
  const { data, x, y } = hover;
  const { left, top } = getTooltipPosition(x, y);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-testid="canopy-portal-tooltip"
      className="pointer-events-none fixed z-[10000] max-w-xs rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs shadow-md"
      style={{ left, top }}
      role="tooltip"
    >
      <div className="font-semibold text-slate-800">{data.label}</div>
      <div className="tabular-nums text-slate-400">{data.id}</div>
      <div className="text-slate-500">{relationLabel(data)}</div>
      {data.role !== 'query' && (
        <div className="tabular-nums text-slate-600">Score: {formatCanopyScore(data.score)}</div>
      )}
    </div>,
    document.body,
  );
}

type CanopyTreeChartProps = {
  tree: CanopyTreeDatum;
  edges: CanopyEdge[];
  labelsById: Record<string, string>;
};

const CanopyTreeChart = memo(function CanopyTreeChart({ tree, edges, labelsById }: CanopyTreeChartProps) {
  const [hover, setHover] = useState<CanopyHoverTooltipState | null>(null);
  const edgesLayer = useMemo(
    () => createEdgesLayer(edges, tree.id, labelsById),
    [edges, labelsById, tree.id],
  );

  return (
    <div
      className="mx-auto w-full bg-white"
      style={{
        height: TREE_HEIGHT_PX,
        minHeight: TREE_HEIGHT_PX,
        maxWidth: TREE_MAX_WIDTH_PX,
      }}
      role="img"
      aria-label="Canopy neighbor tree"
    >
      <ResponsiveTree
        data={tree}
        identity="id"
        mode="tree"
        layout="left-to-right"
        defaultWidth={TREE_MAX_WIDTH_PX}
        defaultHeight={TREE_HEIGHT_PX}
        nodeSize={16}
        activeNodeSize={20}
        inactiveNodeSize={12}
        nodeColor={nodeColor}
        linkCurve="bump"
        linkThickness={2}
        activeLinkThickness={4}
        inactiveLinkThickness={1}
        linkColor={{ from: 'target.color', modifiers: [['opacity', 0.55]] }}
        layers={[edgesLayer, 'nodes', 'labels', 'mesh']}
        enableLabel
        label={nodeLabel}
        labelsPosition="outward"
        orientLabel={false}
        labelOffset={12}
        margin={{ top: 28, right: 280, bottom: 28, left: 120 }}
        motionConfig="gentle"
        isInteractive
        useMesh
        onNodeMouseEnter={(node, event) => {
          setHover({ data: node.data, x: event.clientX, y: event.clientY });
        }}
        onNodeMouseMove={(node, event) => {
          setHover({ data: node.data, x: event.clientX, y: event.clientY });
        }}
        onNodeMouseLeave={() => setHover(null)}
        highlightAncestorNodes
        highlightDescendantNodes
        highlightAncestorLinks
        highlightDescendantLinks
      />
      {hover ? <CanopyHoverTooltip hover={hover} /> : null}
    </div>
  );
});

export function CanopyTabContent({ row = null, className }: CanopyTabContentProps) {
  const userCtx = (userAppContext() ?? {}) as {
    authorization?: string;
    isAuthenticated?: boolean;
    setIsAuthenticated?: (value: boolean) => void;
  };
  const { authorization, isAuthenticated, setIsAuthenticated } = userCtx;

  const queryId = resolveCanopyQueryId(row);
  const [canopyResponse, setCanopyResponse] = useState<CanopyResponse | null>(null);
  const [loadingGraph, setLoadingGraph] = useState(true);
  const [graphError, setGraphError] = useState(false);
  const [namesById, setNamesById] = useState<Record<string, string>>({});
  const [loadingNames, setLoadingNames] = useState(false);

  useEffect(() => {
    if (!queryId) {
      setCanopyResponse(null);
      setLoadingGraph(false);
      setGraphError(false);
      return;
    }

    let cancelled = false;
    setLoadingGraph(true);
    setGraphError(false);
    setCanopyResponse(null);
    setNamesById({});

    const endpoint = buildCanopyGraphEndpoint(queryId, CANOPY_MAX_NEIGHBORS, isAuthenticated);

    fetchEndpoint(endpoint, authorization, setIsAuthenticated)
      .then((response: { status?: string; payload?: unknown } | null) => {
        if (cancelled) return;
        const parsed =
          response?.status === 'OK'
            ? parseCanopyGraphPayload(response.payload ?? response)
            : parseCanopyGraphPayload(response);
        if (parsed) {
          setCanopyResponse(parsed);
          setGraphError(false);
        } else {
          setCanopyResponse(null);
          setGraphError(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setCanopyResponse(null);
        setGraphError(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingGraph(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authorization, isAuthenticated, queryId]);

  const cuis = useMemo(
    () => (canopyResponse ? collectCanopyNodeIds(canopyResponse) : []),
    [canopyResponse],
  );

  useEffect(() => {
    if (cuis.length === 0) {
      setNamesById({});
      setLoadingNames(false);
      return;
    }

    const cached = getCachedDiseaseNames(cuis);
    setNamesById(cached);
    const missing = cuis.filter(cui => !cached[cui]);
    if (missing.length === 0) {
      setLoadingNames(false);
      return;
    }

    let cancelled = false;
    setLoadingNames(true);

    fetchDiseaseNames(cuis, authorization, isAuthenticated, setIsAuthenticated).then(names => {
      if (cancelled) return;
      setNamesById(names);
      setLoadingNames(false);
    });

    return () => {
      cancelled = true;
    };
  }, [authorization, cuis, isAuthenticated]);

  const built = useMemo(
    () => (canopyResponse ? buildCanopyTreeData(applyCanopyDiseaseNames(canopyResponse, namesById)) : null),
    [canopyResponse, namesById],
  );

  if (!queryId) {
    return (
      <p className="px-3 py-6 text-center text-sm text-gray-400" role="status">
        No disease identifier available for canopy.
      </p>
    );
  }

  if (loadingGraph || (loadingNames && !built)) {
    return (
      <div
        className="flex min-h-[120px] items-center justify-center"
        aria-busy="true"
        aria-label="Loading canopy"
        data-testid="canopy-tab-content"
      >
        <Loader isInnerSpinner />
      </div>
    );
  }

  if (graphError || !built) {
    return (
      <p className="px-3 py-6 text-center text-sm text-gray-400" role="status" data-testid="canopy-tab-content">
        Could not load canopy neighbors for this disease.
      </p>
    );
  }

  const { tree, neighborCount, edgeCount, edges } = built;
  const labelsById: Record<string, string> = {
    [tree.id]: tree.label,
    ...Object.fromEntries((tree.children ?? []).map(child => [child.id, child.label])),
  };

  return (
    <div className={className} data-testid="canopy-tab-content">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 px-1">
        <p className="text-xs text-slate-500">
          Neighborhood around <span className="font-medium text-slate-700">{tree.label}</span>
          <span className="ml-2 tabular-nums text-slate-400">
            {neighborCount} neighbors · {edgeCount} relationships
          </span>
        </p>
        <div className="flex flex-wrap gap-3 text-[0.65rem] uppercase tracking-widest text-slate-400">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: PARENT_NODE_COLOR }} />
            Parent
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: CHILD_NODE_COLOR }} />
            Child
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: VIA_NODE_COLOR }} />
            Between neighbors
          </span>
        </div>
      </div>
      <CanopyTreeChart tree={tree} edges={edges} labelsById={labelsById} />
    </div>
  );
}
